import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { School, Check, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { rememberJoin, clearJoin, afterLogin, roleHome } from "@/lib/authDestination";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) rememberJoin(`/invite/${token}`);
    setNeedLogin(!user);
    api.get(`/invites/${token}`).then((r) => setPreview(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invite not found"));
  }, [token, user, loading]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/invites/accept", { token });
      if (data.status === "pending") {
        setSent(data);
        toast.success("Invitation confirmed", { description: "An academy admin has to let you in." });
      } else {
        clearJoin();
        const current = await checkAuth();
        toast.success(`Joined ${data.academy_name} as ${ROLE_LABELS[data.role] || data.role}`);
        navigate(afterLogin(current), { replace: true });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept invite"); }
    finally { setAccepting(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md border border-neutral-800 rounded-sm overflow-hidden" data-testid="invite-accept">
        <div className="bg-black px-6 py-5 border-b border-neutral-800">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-brand-magenta">Trifit.XPS</div>
          <div className="font-heading text-2xl font-black tracking-tight mt-1 flex items-center gap-2"><School className="w-5 h-5" /> Academy invitation</div>
        </div>
        <div className="p-6">
          {loading && <p className="text-sm text-neutral-400">Loading…</p>}

          {needLogin && (
            <div data-testid="invite-need-login">
              <p className="text-sm text-neutral-300 mb-4">Sign in to view and accept your invitation. Use the email address the invite was sent to.</p>
              <Link to={`/login?return_to=${encodeURIComponent(`/invite/${token}`)}`} data-testid="invite-login-link" className="inline-flex items-center justify-center w-full bg-white text-black font-bold px-4 py-2.5 hover:bg-neutral-200 transition-colors">Sign in to continue</Link>
              <p className="text-[11px] text-neutral-500 mt-3">You'll return here automatically after signing in or creating an account.</p>
            </div>
          )}

          {err && <p className="text-sm text-traffic-red flex items-center gap-1.5" data-testid="invite-error"><AlertTriangle className="w-4 h-4" /> {err}</p>}

          {sent && (
            <div data-testid="invite-pending">
              <Check className="w-6 h-6 text-traffic-green" />
              <p className="text-sm text-neutral-300 mt-3">{sent.message}</p>
              <p className="text-xs text-neutral-500 mt-2">
                We passed your account details to {sent.academy_name}. You'll see the squad the moment an admin
                approves you · no data is visible before that.
              </p>
              <Link to={roleHome(user)} data-testid="invite-pending-continue"
                className="inline-flex items-center justify-center w-full mt-5 border border-neutral-700 px-4 py-2.5 hover:bg-neutral-900 transition-colors">Continue</Link>
            </div>
          )}

          {preview && !sent && (
            <div>
              <p className="text-sm text-neutral-400">You've been invited to join</p>
              <div className="font-heading text-xl font-black tracking-tight my-1" data-testid="invite-academy-name">{preview.academy_name}</div>
              <p className="text-sm text-neutral-400 mb-4">as <b className="text-neutral-100" data-testid="invite-role">{ROLE_LABELS[preview.role] || preview.role}</b>{preview.invited_by ? ` · invited by ${preview.invited_by}` : ""}</p>

              {user && !preview.email_matches && (
                <p className="text-xs text-amber-400 border border-amber-400/30 p-3 mb-4" data-testid="invite-email-mismatch">
                  This invite is for <b>{preview.email}</b>. You're signed in as <b>{user?.email}</b>. Sign in with the invited email to accept.
                </p>
              )}
              {preview.accepted && (!user || !preview.email_matches || user.in_academy) && (user && preview.email_matches && user.in_academy ? <div data-testid="invite-already-member"><p className="text-sm text-neutral-300">You're already a member of {preview.academy_name}.</p><button data-testid="invite-open-workspace" onClick={() => { clearJoin(); navigate(roleHome(user), { replace: true }); }} className="inline-flex items-center justify-center w-full mt-4 bg-white text-black font-bold px-4 py-2.5 hover:bg-neutral-200 transition-colors">Open my workspace</button></div> : <p className="text-sm text-neutral-400" data-testid="invite-already">This invite was already accepted. Sign in with the invited email to open your workspace.</p>)}
              {preview.expired && <p className="text-sm text-traffic-red" data-testid="invite-expired">This invite has expired.</p>}

              {(preview.valid || (preview.accepted && user && !user.in_academy)) && preview.email_matches && (
                <button data-testid="invite-accept-btn" onClick={accept} disabled={accepting}
                  className="inline-flex items-center justify-center w-full bg-white text-black font-bold px-4 py-2.5 hover:bg-neutral-200 transition-colors disabled:opacity-40">
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Accept invitation
                </button>
              )}
              {(preview.valid || (preview.accepted && user && !user.in_academy)) && preview.email_matches && (
                <p className="text-[11px] text-neutral-500 mt-3">
                  Your academy and role are already chosen. Accept to open your workspace.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
